import {defineStore} from 'pinia'
import { markRaw } from 'vue'
import * as THREE from 'three'
import type MapCoordinate from '@/types/MapCoordinate.ts'
import { updateCoordinateShips } from '@/models/Map/mapShipScaleAndPositionCalculations.ts'

// Reusable objects to avoid allocation during updates
const _dummy = new THREE.Object3D()
const _blackColor = new THREE.Color(0x000000)
const _tempColor = new THREE.Color()

export const useMapShipStore = defineStore('mapShipStore', {
    state: () => ({
        shipInstancedMesh: null as THREE.InstancedMesh<THREE.BufferGeometry, THREE.ShaderMaterial> | null,
        shipAvailableInstanceIds: markRaw<number[]>([]),
        shipMapCoordinateLookup: markRaw(new Map()),
        shipFleetStatus: markRaw(new Map<number, boolean>()), // Track if a ship is a large fleet (>49 ships)
        elapsedTime: 0 // Track time for pulsating effect
    }),
    actions: {
        initShipInstancedMesh(maxCount: number) {
            // Create a simple triangle geometry using BufferGeometry
            const geometry2 = new THREE.BufferGeometry();
            const vertices = new Float32Array([
                0.0, 0.1, 0.0,  // Top point
                0.0, -0.1, 0.0, // Bottom point
                0.2, 0.0, 0.0   // Right point (more pointy triangle)
            ]);

            geometry2.setAttribute('position', new THREE.BufferAttribute(vertices, 3));

            const material2 = new THREE.ShaderMaterial({
                uniforms: {
                    time: { value: 0.0 } // Uniform for time
                },
                vertexShader: `
        attribute vec3 instanceColor;
        attribute float isLargeFleet;
        uniform float time;
        varying vec3 vColor;
        varying float vIsLargeFleet;
    
        void main() {
          vColor = instanceColor;  // Pass the color to the fragment shader
          vIsLargeFleet = isLargeFleet; // Pass fleet status to fragment shader
          
          // Calculate position
          vec4 mvPosition = modelViewMatrix * instanceMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mvPosition;
        }
    `,
                fragmentShader: `
        varying vec3 vColor;
        varying float vIsLargeFleet;
        uniform float time;
    
        void main() {
          // Discard the fragment if color is default (i.e., black or zero)
          if (vColor.r == 0.0 && vColor.g == 0.0 && vColor.b == 0.0) {
              discard;  // Skip rendering this fragment
          }

          // Final color calculation with pulsating effect for large fleets
          vec3 finalColor = vColor;
          
          // Pulsating effect for large fleets (>49 ships)
          if (vIsLargeFleet > 0.5) {
              // Faster pulsation (4.5) that reaches white at its peak
              float pulseValue = 0.5 + 0.5 * sin(time * 4.5);
              
              // Mix between original color and white based on pulse
              vec3 whiteColor = vec3(1.0, 1.0, 1.0);
              finalColor = mix(finalColor * 0.7, whiteColor, pulseValue * 0.8);
          }
          
          gl_FragColor = vec4(finalColor, 1.0);  // Use the final color for the fragment
        }
    `,
                vertexColors: true,
                transparent: true,
                depthTest: false,
                depthWrite: false
            });

            const colors = new Float32Array(maxCount * 3);  // 3 values per instance (r, g, b)
            geometry2.setAttribute('instanceColor', new THREE.InstancedBufferAttribute(colors, 3));
            
            // Add isLargeFleet attribute for tracking fleets with >49 ships
            const isLargeFleetArray = new Float32Array(maxCount);
            geometry2.setAttribute('isLargeFleet', new THREE.InstancedBufferAttribute(isLargeFleetArray, 1));

            this.shipInstancedMesh = markRaw(new THREE.InstancedMesh(geometry2, material2, maxCount));
            this.shipInstancedMesh.renderOrder = 2; // Higher renderOrder value to render in front of stars
            this.shipInstancedMesh.frustumCulled = false; // Disable frustum culling to prevent disappearing on large maps

            const dummy = new THREE.Object3D();

            for(let i:number = 0; i < maxCount; ++i) {
                dummy.position.set(-10000, -10000, -10000);  // Out of visible range
                dummy.updateMatrix();
                this.shipInstancedMesh.setMatrixAt(i, dummy.matrix);
                this.shipAvailableInstanceIds.push(i);
            }

            this.shipInstancedMesh.instanceMatrix.needsUpdate = true;

            return this.shipInstancedMesh
        },
        addShip(x: number, y: number, color: number):number {
            if(this.shipInstancedMesh) {
                const instanceId = this.getAvailableShipInstanceId();
                if (instanceId !== null && instanceId !== undefined) {
                    // Reuse the global dummy Object3D instance
                    _dummy.position.set(x, y, -0.1);  // Use negative z value to position ships behind planets
                    _dummy.scale.set(1, 1, 1);  // Reset scale
                    _dummy.updateMatrix();

                    // Apply the matrix to the instanced mesh at the specific instance ID
                    this.shipInstancedMesh.setMatrixAt(instanceId, _dummy.matrix);

                    const colorAttribute = this.shipInstancedMesh.geometry.attributes.instanceColor;
                    if (colorAttribute) {
                        // Update the color buffer with the new color using reusable object
                        _tempColor.set(color);
                        colorAttribute.setXYZ(instanceId, _tempColor.r, _tempColor.g, _tempColor.b);
                        colorAttribute.needsUpdate = true;
                    }

                    // Initialize as not a large fleet
                    this.setShipAsFleet(instanceId, false);

                    // Mark the instance matrix for update
                    this.shipInstancedMesh.instanceMatrix.needsUpdate = true;

                    // Store the map coordinate for later reference
                    this.shipMapCoordinateLookup.set(instanceId, { x: x, y: y });

                    return instanceId
                }
            }
            return 0;
        },
        removeShip(instanceId: number) {
            if(this.shipInstancedMesh) {
                // Reuse the global dummy Object3D instance
                // Move the ship out of visible range
                _dummy.position.set(-10000, -10000, -10000);  // Out of visible range
                _dummy.scale.set(1, 1, 1);  // Reset scale
                _dummy.updateMatrix();
                this.shipInstancedMesh.setMatrixAt(instanceId, _dummy.matrix);

                // Reset the color buffer for the instance to black (which the shader will discard)
                const colorAttribute = this.shipInstancedMesh.geometry.attributes.instanceColor;
                if (colorAttribute) {
                    colorAttribute.setXYZ(instanceId, _blackColor.r, _blackColor.g, _blackColor.b);
                    colorAttribute.needsUpdate = true;
                }

                // Reset fleet status
                this.setShipAsFleet(instanceId, false);

                // Mark the instance matrix for update
                this.shipInstancedMesh.instanceMatrix.needsUpdate = true;

                // Release the instance ID back into the pool
                this.releaseShipInstanceId(instanceId);
            }
        },
        getAvailableShipInstanceId(): number {
            if(this.shipAvailableInstanceIds.length === 0) {
                throw new Error('No more available instance ids for ship objects');
            }
            return this.shipAvailableInstanceIds.pop()!;
        },
        releaseShipInstanceId(instanceId: number) {
            this.shipAvailableInstanceIds.push(instanceId)
            this.shipMapCoordinateLookup.delete(instanceId);
            this.shipFleetStatus.delete(instanceId);
        },
        scaleAndPositionShip(instanceId: number, mapCoordinate: MapCoordinate,totalPositions:number, position:number, zoomFactor: number) {
            if(this.shipInstancedMesh) {
                const data = updateCoordinateShips(mapCoordinate, totalPositions, position, zoomFactor);
                _dummy.position.set(data.x, data.y, data.z)  // Use z-position from the updateCoordinateShips function
                _dummy.scale.set(data.scale, data.scale, data.scale);  // Apply uniform scaling
                _dummy.updateMatrix();
                this.shipInstancedMesh.setMatrixAt(instanceId, _dummy.matrix);
                this.shipInstancedMesh.instanceMatrix.needsUpdate = true;
            }
        },
        findCoordinatesByInstanceId(instanceId: number): { x: number, y: number } {
            if(this.shipMapCoordinateLookup.has(instanceId)) {
                return this.shipMapCoordinateLookup.get(instanceId);
            }
            return {x: 0, y: 0}
        },
        // New method to set a ship as a large fleet (>49 ships)
        setShipAsFleet(instanceId: number, isLargeFleet: boolean) {
            if(this.shipInstancedMesh) {
                this.shipFleetStatus.set(instanceId, isLargeFleet);
                
                // Update the isLargeFleet attribute in the shader
                const isLargeFleetAttribute = this.shipInstancedMesh.geometry.attributes.isLargeFleet;
                if (isLargeFleetAttribute) {
                    isLargeFleetAttribute.setX(instanceId, isLargeFleet ? 1.0 : 0.0);
                    isLargeFleetAttribute.needsUpdate = true;
                }
            }
        },
        // Update time uniform for pulsating effect
        updateTime(deltaTime: number) {
            if(this.shipInstancedMesh) {
                this.elapsedTime += deltaTime;
                // Update the time uniform for the shader
                (this.shipInstancedMesh.material as THREE.ShaderMaterial).uniforms.time.value = this.elapsedTime;
            }
        }
    }
});